import React, { useEffect, useState, useContext } from 'react'
import { I18nContext } from 'i18n'
import { Container, Row, Col, Modal } from 'react-bootstrap'
import styled, { css } from 'styled-components'
import { device } from 'utils/device'
import { FaCheck } from 'react-icons/fa'
import Button from '../../atoms/Button'
import { useProgram } from 'hooks/useProgram'
import { saveAs } from 'file-saver'
import Loader from 'react-loader-spinner'
import moment from 'moment'
import { Permission, Alert, CancelButton, CustomLinks } from 'components'
import { checkPermission } from 'utils/permission'

const DownloadScholarshipsModal = ({ show, onHide, filters }) => {
  const { translate } = useContext(I18nContext)
  const [total, setTotal] = useState()
  const {
    executeDownload,
    dataDownload,
    loadingDownload,
    dataPrograms,
    executePrograms,
    loadingPrograms,
  } = useProgram(filters)
  const allowedPermissions = ['canExportScholarships']

  const theMessage = (
    <Alert variant="warning">{translate('download_permission')}</Alert>
  )

  useEffect(() => {
    executePrograms()
  }, [])

  useEffect(() => {
    if (dataPrograms) {
      const { programs } = dataPrograms
      setTotal(programs.length)
    }
  }, [dataPrograms])

  useEffect(() => {
    if (dataDownload) {
      const date = `${moment().format('MMDDYYYY')}-${moment().unix()}`
      let blob = new Blob([dataDownload], {
        type: 'text/csv;charset=utf-8',
      })
      saveAs(blob, `scholarships-${date}.csv`)
      onHide()
    }
  }, [dataDownload])

  const handleDownload = () => {
    if (!checkPermission(allowedPermissions, true)) return
    executeDownload()
  }

  return (
    <ModalContainer show={show} onHide={onHide} backdrop="static">
      <Header closeButton>
        <Modal.Title>
          <ModalTitle>{translate('download_title')}</ModalTitle>
          <ModalSubtitle>
            <CustomLinks location="DOWNLOAD.INFORMATION"/>
          </ModalSubtitle>
        </Modal.Title>
      </Header>
      <Body>
        <Container>
          <DownloadMessage>
            <Permission
              permission={allowedPermissions}
              message={theMessage}
              every
            >
              <>
                <Col md="1">
                  <CheckIcon />
                </Col>
                <Col>
                  {loadingPrograms && (
                    <Loader
                      type="Oval"
                      color="#007681"
                      height={20}
                      timeout={20000}
                    />
                  )}
                  {!loadingPrograms && (
                    <>
                      <div>{translate('download_ready')}</div>
                      <div>{translate('download_records', { total })}</div>
                    </>
                  )}
                </Col>
              </>
            </Permission>
          </DownloadMessage>
          <Tabs>
            <Tab md="2" highlight="true">
              {translate('download_overview')}
            </Tab>
            <Tab md="2">{translate('download_options')}</Tab>
          </Tabs>
          <Row>
            <TabContent>{translate('download_content')}</TabContent>
          </Row>
        </Container>
      </Body>
      <ModalFooter>
        <Button
          primary="true"
          onClick={handleDownload}
          disabled={!checkPermission(allowedPermissions, true) || loadingPrograms}
        >
          {loadingDownload ? (
            <Loader type="Oval" color="#FFF" height={20} timeout={20000} />
          ) : (
            <span>{translate('download')}</span>
          )}
        </Button>

        <CancelButton onClick={onHide} />
      </ModalFooter>
    </ModalContainer>
  )
}

const ModalFooter = styled(Modal.Footer)`
  border: 0;
  justify-content: center;

  > button:last-child {
    color: gray;
  }
`

const ModalContainer = styled(Modal)`
  font-size: ${({ theme }) => theme.sizes.normal};
  color: ${({ theme }) => theme.palette.grayscale[1]};

  .modal-content {
    border-radius: 0.8rem;
    padding: 1rem 2rem;
    border: 0;
  }

  > div {
    max-width: 700px;
  }

  input,
  textarea {
    font-size: 0.8rem;
  }

  .form-row > .col {
    margin-right: 2.5rem;
    @media ${device.mobileL} {
      margin-right: 0;
    }
  }
`

const Body = styled(Modal.Body)`
  padding: 0;
`

const Header = styled(Modal.Header)`
  border: 0;

  > .close {
    position: absolute;
    right: 1rem;
    top: 1rem;
  }

  a {
    color: ${({ theme }) => theme.palette.primary[2]};
  }
`

const ModalTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
`

const ModalSubtitle = styled.div`
  font-size: 1rem;
  font-weight: 300;

  a {
    color: ${({ theme }) => theme.link.color};
  }
`

const CheckIcon = styled(FaCheck)`
  font-size: 2rem;
  color: ${(props) => props.theme.link.color};
`

const DownloadMessage = styled(Row)`
  margin: 3rem 0;
`

const Tabs = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale[7]};
`

const Tab = styled(Col)`
  color: ${({ theme }) => theme.palette.grayscale[7]};
  text-align: center;
  padding: 0.3rem;
  ${(props) =>
    props.highlight &&
    css`
      color: ${({ theme }) => theme.palette.grayscale[2]};
      border-bottom: 3px solid ${props.theme.link.color};
      font-weight: 600;
    `}
`

const TabContent = styled(Col)`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.grayscale[3]};
  padding: 1rem 0;
`

export default DownloadScholarshipsModal
